import React, { useRef, useCallback } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';

/**
 * Wraps a Button (or any element) with a magnetic pull effect.
 * When the cursor enters a ~radius around the button, the button
 * subtly shifts toward the cursor, creating an "attracted" feel.
 *
 * Props:
 *   strength - how many px the button can shift (default 6)
 *   radius   - the hover zone extends this far beyond the button (default 40)
 */
const MagneticButton = React.memo(({ children, strength = 6, radius = 40 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const ref = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    // Normalize by the padded zone size so the pull is proportional
    const zone = Math.max(rect.width, rect.height) / 2 + radius;
    const pullX = (dx / zone) * strength;
    const pullY = (dy / zone) * strength;
    ref.current.style.transform = `translate(${pullX}px, ${pullY}px)`;
  }, [strength, radius]);

  const handleMouseLeave = useCallback(() => {
    if (!ref.current) return;
    ref.current.style.transform = 'translate(0px, 0px)';
  }, []);

  // On mobile/touch devices, hover effects don't apply — render children directly
  if (isMobile) return <>{children}</>;

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      {/* Invisible hover zone extending beyond the button — no layout impact */}
      <Box
        sx={{
          position: 'absolute',
          inset: `-${radius}px`,
          zIndex: 0,
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
      <Box
        ref={ref}
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'inline-block',
          transition: 'transform 0.25s cubic-bezier(0.23, 1, 0.32, 1)',
          willChange: 'transform',
        }}
      >
        {children}
      </Box>
    </Box>
  );
});

export default MagneticButton;
